<script>
import Overlay from '@/assets/js/src/components/Overlay.vue'
import {
    emailRules, termsRules,
} from '@/assets/js/src/util/validation'

import LoadingDummyP from '@/assets/js/src/components/loading/LoadingDummyP'
import { usePrefetchData, } from '@/assets/js/src/util/composables/usePrefetchData'
import LoadingDummyCustom from '@/assets/js/src/components/loading/LoadingDummyCustom'
import LoadingDummySelect from '@/assets/js/src/components/loading/LoadingDummySelect'
import { useNewsletterActionsStore, } from '@/assets/js/src/modules/newsletter/_pinia/newsletterActions'
import H1Title from '@/assets/js/src/components/overlay/H1Title.vue'
import { useHeadInfoTitle, } from '@/assets/js/src/layout/useHeadInfo'
import { useTranslation, } from '@/assets/js/src/util/composables/useTranslation'


export default {
    name: 'NewsletterOverlay',
    components: { H1Title, LoadingDummySelect, LoadingDummyCustom, LoadingDummyP, Overlay, },
    setup () {
        let { messagesLoaded, } = usePrefetchData({
            loadAsyncMessages: [ 'newsletter', 'user', 'common', ],
        })

        useHeadInfoTitle({
            titleKey: 'async.newsletter.title',
            descriptionKey: 'async.user.profile.options.newsletter.teaser',
        })

        return {
            messagesLoaded,
            ...useTranslation(),
        }
    },
    data () {
        return {
            valid: true,
            email: '',
            gender: '',
            firstname: '',
            lastname: '',
            emailRules,
            terms: false,
            termsRules,
            loading: false,
        }
    },
    computed: {
        genderEnum () {
            return (this.messagesLoaded) ? [
                { title: '', value: null, },
                { title: this.t('async.user.form.gender.m'), value: 'm', },
                { title: this.t('async.user.form.gender.f'), value: 'f', },
            ] : []
        },
    },
    methods: {
        async submit () {
            let { valid: isValid, } = await this.$refs.form.validate()
            if (!isValid) {
                return
            }

            this.loading = true
            try {
                await useNewsletterActionsStore().register({
                    data: {
                        email: this.email,
                        firstname: this.firstname || '',
                        lastname: this.lastname || '',
                        gender: this.gender || '',
                    },
                })
            } finally {
                this.loading = false
                this.$refs.form.reset()
            }
        },
    },
}
</script>

<template>
    <overlay
        class-name="newsletter"
    >
        <div class="d-flex hidden-print-only">
            <h1-title title-key="async.newsletter.title" />
        </div>
        <v-row no-gutters>
            <v-col
                md="6"
                cols="12"
                class="newsletter__form"
            >
                <v-form
                    id="newsletter"
                    ref="form"
                    v-model="valid"
                    @keyup.enter="submit"
                >
                    <v-text-field
                        v-if="messagesLoaded"
                        v-model="email"
                        :label="t('async.user.form.email.label')"
                        :rules="emailRules"
                        bg-color="background"
                        color="primary"
                        type="email"
                        class="email-field"
                    ></v-text-field>
                    <loading-dummy-select
                        v-else
                    ></loading-dummy-select>
                    <v-select
                        v-if="messagesLoaded"
                        v-model="gender"
                        bg-color="background"
                        color="primary"
                        :items="genderEnum"
                        :label="t('async.user.form.gender.label')"
                    ></v-select>
                    <loading-dummy-select
                        v-else
                    ></loading-dummy-select>
                    <v-text-field
                        v-if="messagesLoaded"
                        v-model="firstname"
                        bg-color="background"
                        color="primary"
                        :label="t('async.user.form.firstname.label')"
                    ></v-text-field>
                    <loading-dummy-select
                        v-else
                    ></loading-dummy-select>
                    <v-text-field
                        v-if="messagesLoaded"
                        v-model="lastname"
                        bg-color="background"
                        color="primary"
                        :label="t('async.user.form.lastname.label')"
                    ></v-text-field>
                    <loading-dummy-select
                        v-else
                    ></loading-dummy-select>
                    <v-checkbox
                        v-if="messagesLoaded"
                        v-model="terms"
                        :rules="termsRules"
                        color="primary"
                        required
                    >
                        <template #label>
                            <div class="flex-wrap">
                                {{ t('async.user.form.privacy.label.title') }}
                                <small>{{ t('async.newsletter.form_privacy') }}</small>
                            </div>
                        </template>
                    </v-checkbox>
                    <loading-dummy-p
                        v-else
                        key-prefix="dummy_1"
                        count="5"
                    ></loading-dummy-p>

                    <v-btn
                        v-if="messagesLoaded"
                        class="newsletter__submit"
                        color="primary"
                        type="submit"
                        :disabled="!valid || loading"
                        :loading="loading"
                        @click="submit"
                    >
                        {{ t('async.newsletter.subscribe') }}
                    </v-btn>
                    <loading-dummy-custom
                        v-else
                        height="56px"
                        margin="16px 0 0"
                    ></loading-dummy-custom>
                </v-form>
            </v-col>
            <v-col
                md="6"
                cols="12"
                class="order-1 pl-0 order-md-2 pl-md-4"
            >
                <div
                    v-if="messagesLoaded"
                    v-html="t('async.newsletter.teaser')"
                ></div>
                <template
                    v-else
                >
                    <loading-dummy-p
                        count="4"
                    ></loading-dummy-p>
                </template>
            </v-col>
        </v-row>
    </overlay>
</template>

<style lang="scss">
.bs-app .newsletter {
    &__form {
        order: 1;
        padding-right: 16px;

        @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
            order: 2;
            padding: 0;
        }

        .email-field {
            margin-bottom: 8px;

            label {
                padding-right: 8px;

                &::after {
                    position: absolute;
                    top: -2px;
                    right: 0;
                    display: block;
                    content: "*";
                }
            }
        }

        .v-checkbox {
            .v-selection-control {
                align-items: flex-start;
            }

            .error--text {
                small {
                    color: map-deep-get($bs-color, form, error) !important;
                    caret-color: map-deep-get($bs-color, form, error) !important;
                }
            }
        }
    }

    &__submit {
        margin-top: 32px;
        margin-left: 0;
    }
}


</style>
